import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { perusahaanApi } from '../api/perusahaan';
import { ApiException } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { useDebounce } from '../hooks/useDebounce';
import { Alert } from '../components/Alert';
import { Button } from '../components/Button';
import { AccentCard, Card } from '../components/Card';
import { Checkbox } from '../components/Checkbox';
import { Input } from '../components/Input';
import { AppLayout } from '../components/Layout';
import { Select } from '../components/Select';

type IdStatus =
  | { state: 'idle' }
  | { state: 'checking' }
  | { state: 'available' }
  | { state: 'unavailable'; reason: string };

const currentYear = new Date().getFullYear();
const tahunOptions = [currentYear - 4, currentYear - 3, currentYear - 2, currentYear - 1].map((y) => ({
  value: String(y),
  label: String(y),
}));

export function DaftarPerusahaanPage() {
  const navigate = useNavigate();
  const { klien } = useAuth();

  // Section 1: Identitas
  const [idPerusahaan, setIdPerusahaan] = useState('');
  const [namaPerusahaan, setNamaPerusahaan] = useState('');
  const [npwp, setNpwp] = useState('');
  const [jenisWp, setJenisWp] = useState('');
  const [dagang, setDagang] = useState(false);
  const [manufaktur, setManufaktur] = useState(false);
  const [jasa, setJasa] = useState(false);
  const [namaDirektur, setNamaDirektur] = useState('');
  const [alamat, setAlamat] = useState('');
  const [emailPerusahaan, setEmailPerusahaan] = useState('');
  const [pic, setPic] = useState('');

  // Section 2: SKT
  const [nomorSkt, setNomorSkt] = useState('');
  const [tanggalSkt, setTanggalSkt] = useState('');

  // Section 3: Pengaturan Sistem (YA/TIDAK & Customize di-disable sesuai V2/Booku Lama)
  const [sistemApproval] = useState('TIDAK');
  const [sistemCoa] = useState('Standar Aplikasi');
  const [tahunCutOff, setTahunCutOff] = useState(String(currentYear - 1));

  // Section 4: Super User Aplikasi
  // Username SELALU = username klien (anchor untuk auto-login dari portal).
  // Field di-disable di UI; backend juga menolak nilai lain (defense-in-depth).
  const [suPassword, setSuPassword] = useState('');
  const [suConfirm, setSuConfirm] = useState('');
  const [suNamaLengkap, setSuNamaLengkap] = useState(klien?.namaLengkap ?? '');

  // Status check-id
  const [idStatus, setIdStatus] = useState<IdStatus>({ state: 'idle' });
  const debouncedId = useDebounce(idPerusahaan.trim().toLowerCase(), 500);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Real-time check-id
  useEffect(() => {
    if (!debouncedId) {
      setIdStatus({ state: 'idle' });
      return;
    }
    if (debouncedId.length !== 8) {
      setIdStatus({ state: 'unavailable', reason: 'Harus tepat 8 karakter.' });
      return;
    }

    let cancelled = false;
    setIdStatus({ state: 'checking' });
    perusahaanApi
      .checkId(debouncedId)
      .then((r) => {
        if (cancelled) return;
        if (r.available) setIdStatus({ state: 'available' });
        else setIdStatus({ state: 'unavailable', reason: r.reason ?? 'Tidak tersedia.' });
      })
      .catch((err) => {
        if (cancelled) return;
        setIdStatus({
          state: 'unavailable',
          reason: err instanceof ApiException ? err.message : 'Gagal cek ketersediaan.',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (idStatus.state !== 'available') {
      setError('ID Perusahaan belum tersedia atau belum dicek.');
      return;
    }
    if (!dagang && !manufaktur && !jasa) {
      setError('Minimal 1 Jenis Usaha harus dipilih (Dagang / Manufaktur / Jasa).');
      return;
    }
    if (suPassword !== suConfirm) {
      setError('Password Super User dan konfirmasi tidak cocok.');
      return;
    }

    setLoading(true);
    try {
      const result = await perusahaanApi.register({
        idPerusahaan: idPerusahaan.trim().toLowerCase(),
        namaPerusahaan: namaPerusahaan.trim(),
        npwp: npwp.trim(),
        jenisWp,
        dagang,
        manufaktur,
        jasa,
        namaDirektur: namaDirektur.trim(),
        alamatPerusahaan: alamat.trim(),
        emailPerusahaan: emailPerusahaan.trim().toLowerCase(),
        pic: pic.trim(),
        nomorSkt: nomorSkt.trim() || undefined,
        tanggalSkt: tanggalSkt || undefined,
        sistemApproval,
        sistemCoa,
        tahunCutOff: Number(tahunCutOff),
        superUser: {
          // Backend mem-paksa username = username klien dari JWT,
          // value yang dikirim di sini diabaikan (defense-in-depth).
          username: klien?.username ?? '',
          password: suPassword,
          namaLengkap: suNamaLengkap.trim(),
        },
      });
      setSuccess(result.message);
      setTimeout(() => navigate('/dashboard', { replace: true }), 2500);
    } catch (err) {
      setError(err instanceof ApiException ? err.message : 'Terjadi kesalahan tak terduga.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppLayout>
      <AccentCard
        title="Daftarkan Perusahaan"
        subtitle="Lengkapi informasi perusahaan untuk dikelola di Booku"
      >
        {success && (
          <div className="mb-4">
            <Alert variant="success" title="Pendaftaran berhasil">
              {success}
              <div className="mt-1 text-xs">Anda akan diarahkan ke dashboard...</div>
            </Alert>
          </div>
        )}

        {error && (
          <div className="mb-4">
            <Alert variant="error">{error}</Alert>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* ===== SECTION 1: IDENTITAS ===== */}
          <section>
            <h3 className="text-xs font-semibold text-booku-primary tracking-wider mb-4">
              IDENTITAS PERUSAHAAN
            </h3>

            <div className="space-y-4">
              <div>
                <Input
                  label="ID Perusahaan"
                  name="idPerusahaan"
                  value={idPerusahaan}
                  onChange={(e) => setIdPerusahaan(e.target.value.toLowerCase())}
                  placeholder="8 karakter, huruf kecil + angka, diawali huruf"
                  maxLength={8}
                  required
                  disabled={loading || !!success}
                  hint={
                    idStatus.state === 'idle'
                      ? 'Contoh: tokoabc1, ptmakmur, cv0001aa'
                      : idStatus.state === 'checking'
                      ? 'Memeriksa ketersediaan...'
                      : idStatus.state === 'available'
                      ? '✓ Tersedia'
                      : `✗ ${idStatus.reason}`
                  }
                  error={idStatus.state === 'unavailable' ? idStatus.reason : undefined}
                />
              </div>

              <Input
                label="Nama Perusahaan"
                value={namaPerusahaan}
                onChange={(e) => setNamaPerusahaan(e.target.value)}
                required
                maxLength={72}
                disabled={loading || !!success}
              />

              <div className="grid md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Input
                    label="NPWP"
                    value={npwp}
                    onChange={(e) => setNpwp(e.target.value)}
                    placeholder="01.234.567.8-901.000"
                    required
                    disabled={loading || !!success}
                  />
                </div>
                <Select
                  label="Jenis WP"
                  value={jenisWp}
                  onChange={(e) => setJenisWp(e.target.value)}
                  placeholder="Pilih..."
                  required
                  disabled={loading || !!success}
                  options={[
                    { value: 'Orang Pribadi', label: 'Orang Pribadi' },
                    { value: 'Badan Hukum', label: 'Badan Hukum' },
                  ]}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Jenis Usaha <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-6">
                  <Checkbox
                    label="Dagang"
                    checked={dagang}
                    onChange={(e) => setDagang(e.target.checked)}
                    disabled={loading || !!success}
                  />
                  <Checkbox
                    label="Manufaktur"
                    checked={manufaktur}
                    onChange={(e) => setManufaktur(e.target.checked)}
                    disabled={loading || !!success}
                  />
                  <Checkbox
                    label="Jasa"
                    checked={jasa}
                    onChange={(e) => setJasa(e.target.checked)}
                    disabled={loading || !!success}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Pilih minimal 1.</p>
              </div>

              <Input
                label="Nama Direktur"
                value={namaDirektur}
                onChange={(e) => setNamaDirektur(e.target.value)}
                required
                disabled={loading || !!success}
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Alamat Perusahaan <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={alamat}
                  onChange={(e) => setAlamat(e.target.value)}
                  required
                  rows={3}
                  disabled={loading || !!success}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm
                             focus:outline-none focus:ring-2 focus:ring-booku-primary focus:border-booku-primary
                             disabled:bg-gray-50"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <Input
                  label="Email Perusahaan"
                  type="email"
                  value={emailPerusahaan}
                  onChange={(e) => setEmailPerusahaan(e.target.value)}
                  required
                  disabled={loading || !!success}
                />
                <Input
                  label="PIC (Person In Charge)"
                  value={pic}
                  onChange={(e) => setPic(e.target.value)}
                  required
                  disabled={loading || !!success}
                />
              </div>
            </div>
          </section>

          {/* ===== SECTION 2: SKT ===== */}
          <section className="pt-2 border-t border-gray-100">
            <h3 className="text-xs font-semibold text-booku-primary tracking-wider mb-4">
              DATA SKT (OPSIONAL)
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Input
                label="Nomor SKT"
                value={nomorSkt}
                onChange={(e) => setNomorSkt(e.target.value)}
                disabled={loading || !!success}
              />
              <Input
                label="Tanggal SKT"
                type="date"
                value={tanggalSkt}
                onChange={(e) => setTanggalSkt(e.target.value)}
                disabled={loading || !!success}
              />
            </div>
          </section>

          {/* ===== SECTION 3: PENGATURAN SISTEM ===== */}
          <section className="pt-2 border-t border-gray-100">
            <h3 className="text-xs font-semibold text-booku-primary tracking-wider mb-4">
              PENGATURAN SISTEM
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              <Input
                label="Sistem Approval"
                value={sistemApproval}
                disabled
                hint="Fitur 'YA' belum tersedia."
              />
              <Input
                label="Sistem COA"
                value={sistemCoa}
                disabled
                hint="Customize belum tersedia."
              />
              <Select
                label="Tahun Cut Off"
                value={tahunCutOff}
                onChange={(e) => setTahunCutOff(e.target.value)}
                options={tahunOptions}
                disabled={loading || !!success}
                hint="Cut off: 31 Desember tahun ini."
              />
            </div>
          </section>

          {/* ===== SECTION 4: SUPER USER APLIKASI ===== */}
          <section className="pt-2 border-t border-gray-100">
            <h3 className="text-xs font-semibold text-booku-primary tracking-wider mb-4">
              SUPER USER APLIKASI BOOKU
            </h3>
            <Card className="bg-yellow-50 border-yellow-200 shadow-none">
              <p className="text-xs text-yellow-800">
                Super User aplikasi otomatis pakai <strong>username akun klien Anda</strong> — sehingga
                Anda bisa <strong>login otomatis</strong> ke aplikasi Booku langsung dari portal ini
                (tanpa input password). Password manual hanya dipakai sebagai cadangan
                (mis. saat akses dari device tanpa portal).
              </p>
            </Card>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <Input
                label="Username Aplikasi"
                value={klien?.username ?? ''}
                disabled
                hint="Otomatis = username klien Anda. Tidak bisa diubah."
              />
              <Input
                label="Nama Lengkap User"
                value={suNamaLengkap}
                onChange={(e) => setSuNamaLengkap(e.target.value)}
                required
                disabled={loading || !!success}
              />
              <Input
                label="Password Manual (cadangan)"
                type="password"
                value={suPassword}
                onChange={(e) => setSuPassword(e.target.value)}
                placeholder="Minimal 8 karakter"
                required
                disabled={loading || !!success}
                hint="Hanya dipakai kalau login manual (tanpa portal)"
              />
              <Input
                label="Konfirmasi Password"
                type="password"
                value={suConfirm}
                onChange={(e) => setSuConfirm(e.target.value)}
                required
                disabled={loading || !!success}
              />
            </div>
          </section>

          <div className="pt-4 flex gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/dashboard')}
              disabled={loading}
            >
              Batal
            </Button>
            <Button type="submit" variant="primary" loading={loading} disabled={!!success} fullWidth>
              Daftarkan Perusahaan
            </Button>
          </div>
        </form>
      </AccentCard>
    </AppLayout>
  );
}
